/**
 * Firms & Entitlements (Super Admin).
 * Onboard PE firms and their businesses, and package what each has
 * purchased. Deny-by-default: anything not listed never runs and stays
 * hidden from the dashboard. Replaces the standalone entitlements-console.
 */
import React, { useEffect, useState } from 'react';
import { Building2, Plus, Users as UsersIcon, Trash2, X, ShieldCheck, Link2 } from 'lucide-react';
import { API_BASE, apiFetch } from '../api/base';

interface Firm {
  firmId: string;
  name: string;
  defaultWorkstreams?: string[] | null;
}
interface TenantEntitlement {
  portcoId: string;
  name?: string;
  firmId?: string;
  source: 'tenant' | 'firm' | 'none';
  entitledWorkstreams: string[];
}

async function api<T = any>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await apiFetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(opts.headers as Record<string, string>) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Request failed (${res.status})`);
  return data;
}

const Chip: React.FC<{ label: string; muted?: boolean }> = ({ label, muted }) => (
  <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap border ${
    muted ? 'bg-slate-50 text-slate-500 border-slate-200' : 'bg-mantis-50 text-mantis-700 border-mantis-200'}`}>
    {label}
  </span>
);

const SourceBadge: React.FC<{ source: string }> = ({ source }) => {
  const map: Record<string, string> = {
    tenant: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    firm: 'bg-sky-50 text-sky-700 border-sky-200',
    none: 'bg-red-50 text-red-700 border-red-200',
  };
  return <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-bold border ${map[source] || map.none}`}>
    {source === 'tenant' ? 'PURCHASED' : source === 'firm' ? 'FIRM DEFAULT' : 'NONE'}
  </span>;
};

const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }> = ({ title, onClose, children, wide }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 px-4" onClick={onClose}>
    <div className={`bg-white rounded-xl border border-slate-200 shadow-xl w-full ${wide ? 'max-w-xl' : 'max-w-md'} max-h-[85vh] overflow-y-auto`}
      onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
        <h3 className="text-sm font-bold text-slate-800">{title}</h3>
        <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

const inputCls = 'w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 focus:outline-none focus:border-mantis-500 bg-white';

export const EntitlementsPage: React.FC = () => {
  const [catalog, setCatalog] = useState<string[]>([]);
  const [firms, setFirms] = useState<Firm[]>([]);
  const [tenants, setTenants] = useState<TenantEntitlement[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ msg: string; err?: boolean } | null>(null);

  const notify = (msg: string, err?: boolean) => {
    setToast({ msg, err });
    setTimeout(() => setToast(null), 3500);
  };

  const load = async () => {
    setLoading(true);
    try {
      const d = await api('/entitlements');
      setCatalog(d.catalog || []);
      setFirms(d.firms || []);
      setTenants(d.tenants || []);
    } catch (e: any) {
      notify(e?.message || 'Failed to load entitlements', true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const portcosOf = (firmId: string) => tenants.filter((t) => t.firmId === firmId);

  // ---- entitlements edit ----
  const [edit, setEdit] = useState<{ kind: 'tenant' | 'firm'; id: string; name: string; current: string[] | null } | null>(null);
  const [editSel, setEditSel] = useState<string[]>([]);

  const openEdit = (kind: 'tenant' | 'firm', id: string) => {
    if (kind === 'tenant') {
      const t = tenants.find((x) => x.portcoId === id)!;
      setEdit({ kind, id, name: t.name || id, current: t.source === 'tenant' ? t.entitledWorkstreams : null });
      setEditSel(t.source === 'tenant' ? t.entitledWorkstreams : []);
    } else {
      const f = firms.find((x) => x.firmId === id)!;
      setEdit({ kind, id, name: f.name, current: f.defaultWorkstreams || null });
      setEditSel(f.defaultWorkstreams || []);
    }
  };

  const saveEdit = async (clear = false) => {
    if (!edit) return;
    try {
      await api(`/entitlements/${edit.kind}/${edit.id}`, {
        method: 'PUT',
        body: JSON.stringify(clear ? { clear: true } : { workstreams: editSel }),
      });
      setEdit(null);
      await load();
      notify(clear ? 'Cleared — now inheriting the firm default' : `Saved ${editSel.length} workstream${editSel.length === 1 ? '' : 's'} for ${edit.name}`);
    } catch (e: any) { notify(e?.message || 'Save failed', true); }
  };

  // ---- new firm ----
  const [newFirm, setNewFirm] = useState<{ name: string; code: string } | null>(null);

  const createFirm = async () => {
    try {
      await api('/firms', { method: 'POST', body: JSON.stringify({ name: newFirm!.name, code: newFirm!.code || undefined }) });
      setNewFirm(null);
      await load();
      notify(`Firm created: ${newFirm!.name}. Assign businesses and set defaults next.`);
    } catch (e: any) { notify(e?.message || 'Failed to create firm', true); }
  };

  // ---- assign businesses ----
  const [assign, setAssign] = useState<{ firmId: string; name: string } | null>(null);
  const [assignSel, setAssignSel] = useState<string[]>([]);

  const openAssign = (firmId: string, name: string) => {
    setAssign({ firmId, name });
    setAssignSel(tenants.filter((t) => t.firmId === firmId).map((t) => t.portcoId));
  };

  const saveAssign = async () => {
    try {
      const r = await api(`/firms/${assign!.firmId}/tenants`, { method: 'POST', body: JSON.stringify({ portcoIds: assignSel, sync: true }) });
      const detached = r?.detached?.length ? ` (${r.detached.length} detached)` : '';
      setAssign(null);
      await load();
      notify(`Businesses updated for ${assign!.name}${detached}`);
    } catch (e: any) { notify(e?.message || 'Assign failed', true); }
  };

  // ---- create firm admin ----
  const [adminFor, setAdminFor] = useState<{ firmId: string; name: string } | null>(null);
  const [adminForm, setAdminForm] = useState({ name: '', email: '', password: '' });

  const createAdmin = async () => {
    try {
      await api('/auth/users', {
        method: 'POST',
        body: JSON.stringify({ ...adminForm, role: 'tenant_admin', firmId: adminFor!.firmId }),
      });
      setAdminFor(null);
      setAdminForm({ name: '', email: '', password: '' });
      notify(`Firm admin created for ${adminFor!.name}. They sign in at this portal with the temporary password.`);
    } catch (e: any) { notify(e?.message || 'Failed to create admin', true); }
  };

  // ---- new business ----
  const [newBiz, setNewBiz] = useState<{ name: string; id: string; firmId: string } | null>(null);

  const createBiz = async () => {
    let portcoId = newBiz!.id.trim();
    if (!portcoId) portcoId = 'portco_' + newBiz!.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 30);
    try {
      await api('/integrations/onboard', { method: 'POST', body: JSON.stringify({ portcoId, name: newBiz!.name }) });
      if (newBiz!.firmId) await api(`/firms/${newBiz!.firmId}/tenants`, { method: 'POST', body: JSON.stringify({ portcoIds: [portcoId] }) });
      setNewBiz(null);
      await load();
      notify(`Business created: ${newBiz!.name} (${portcoId}). Connect its EHR in Settings → Integrations.`);
    } catch (e: any) { notify(e?.message || 'Failed to create business', true); }
  };

  // ---- deletes ----
  const deleteFirm = async (f: Firm) => {
    if (!window.confirm(`Delete firm "${f.name}"? Only possible while it has no businesses.`)) return;
    try { await api(`/firms/${f.firmId}`, { method: 'DELETE' }); await load(); notify(`Firm deleted: ${f.name}`); }
    catch (e: any) { notify(e?.message || 'Delete failed', true); }
  };
  const deleteBiz = async (t: TenantEntitlement) => {
    if (!window.confirm(`Delete business "${t.name || t.portcoId}" (${t.portcoId})? Only possible while it has no integrations, users, or data.`)) return;
    try { await api(`/integrations/tenants/${t.portcoId}`, { method: 'DELETE' }); await load(); notify(`Business deleted: ${t.name || t.portcoId}`); }
    catch (e: any) { notify(e?.message || 'Delete failed', true); }
  };

  const ghostBtn = 'inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-slate-600 hover:text-mantis-600 border border-slate-200 rounded-lg bg-white';
  const dangerBtn = 'inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-slate-600 hover:text-red-600 border border-slate-200 rounded-lg bg-white';
  const primaryBtn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-mantis-600 hover:bg-mantis-500 text-white rounded-lg transition-colors';

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800 mb-1">Firms & Entitlements</h1>
          <p className="text-xs text-slate-500">
            Onboard PE firms and their businesses, and package what each has purchased. Deny-by-default: anything not listed never runs and stays hidden.
          </p>
        </div>
        {toast && (
          <div className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border ${
            toast.err ? 'bg-red-50 border-red-200 text-red-700' : 'bg-mantis-50 border-mantis-200 text-mantis-700'}`}>
            <ShieldCheck className="w-3.5 h-3.5" /> <span>{toast.msg}</span>
          </div>
        )}
      </div>

      {/* Firms */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-bold text-slate-800">Firms <span className="font-normal text-slate-400">— the PE firms on the platform</span></h2>
            <p className="text-xs text-slate-500 mt-0.5">A firm sets the default workstreams its businesses inherit, and scopes its firm admins to its businesses. Businesses with an explicit PURCHASED list are not affected by defaults.</p>
          </div>
          <button onClick={() => setNewFirm({ name: '', code: '' })} className={primaryBtn}><Plus className="w-3.5 h-3.5" /> New firm</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="border-b-2 border-slate-200 text-slate-500 uppercase tracking-wider text-[10px]">
              <tr><th className="py-2 px-2">Firm</th><th className="py-2 px-2">Businesses</th><th className="py-2 px-2">Default workstreams</th><th className="py-2 px-2 text-right">Actions</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {firms.map((f) => {
                const pcs = portcosOf(f.firmId);
                return (
                  <tr key={f.firmId} className="hover:bg-slate-50 transition-colors">
                    <td className="py-2.5 px-2">
                      <div className="font-semibold text-slate-800">{f.name}</div>
                      <div className="text-slate-400 text-[10px]">{f.firmId}</div>
                    </td>
                    <td className="py-2.5 px-2">
                      <div className="flex flex-wrap gap-1 max-w-56">
                        {pcs.length ? pcs.map((p) => <Chip key={p.portcoId} label={p.name || p.portcoId} muted />) : <span className="text-slate-400 text-[11px]">none yet</span>}
                      </div>
                    </td>
                    <td className="py-2.5 px-2">
                      <div className="flex flex-wrap gap-1 max-w-56">
                        {(f.defaultWorkstreams || []).length
                          ? (f.defaultWorkstreams || []).map((w) => <Chip key={w} label={w} />)
                          : <span className="text-slate-400 text-[11px]">none — businesses inherit nothing</span>}
                      </div>
                    </td>
                    <td className="py-2.5 px-2">
                      <div className="flex items-center justify-end gap-1.5 flex-wrap">
                        <button onClick={() => openEdit('firm', f.firmId)} className={ghostBtn}>Edit defaults</button>
                        <button onClick={() => openAssign(f.firmId, f.name)} className={ghostBtn}><Link2 className="w-3 h-3" /> Assign</button>
                        <button onClick={() => { setAdminFor({ firmId: f.firmId, name: f.name }); setAdminForm({ name: '', email: '', password: '' }); }} className={ghostBtn}><UsersIcon className="w-3 h-3" /> Create admin</button>
                        {pcs.length === 0 && <button onClick={() => deleteFirm(f)} className={dangerBtn}><Trash2 className="w-3 h-3" /></button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {firms.length === 0 && !loading && <tr><td colSpan={4} className="py-6 text-center text-slate-400">No firms yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Businesses */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-bold text-slate-800">Businesses (PortCos)</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Source shows where the workstream list comes from: <b>PURCHASED</b> = explicit list · <b>FIRM DEFAULT</b> = inherited from firm · <b>NONE</b> = nothing purchased (all agents hidden and skipped).
            </p>
          </div>
          <button onClick={() => setNewBiz({ name: '', id: '', firmId: '' })} className={primaryBtn}><Plus className="w-3.5 h-3.5" /> New business</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="border-b-2 border-slate-200 text-slate-500 uppercase tracking-wider text-[10px]">
              <tr><th className="py-2 px-2">Business</th><th className="py-2 px-2">Firm</th><th className="py-2 px-2">Source</th><th className="py-2 px-2">Purchased workstreams</th><th className="py-2 px-2 text-right">Actions</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {tenants.map((t) => {
                const firmName = firms.find((f) => f.firmId === t.firmId)?.name || '—';
                return (
                  <tr key={t.portcoId} className="hover:bg-slate-50 transition-colors">
                    <td className="py-2.5 px-2">
                      <div className="font-semibold text-slate-800">{t.name || t.portcoId}</div>
                      <div className="text-slate-400 text-[10px]">{t.portcoId}</div>
                    </td>
                    <td className="py-2.5 px-2 text-slate-600">{firmName}</td>
                    <td className="py-2.5 px-2"><SourceBadge source={t.source} /></td>
                    <td className="py-2.5 px-2">
                      <div className="flex flex-wrap gap-1 max-w-64">
                        {t.entitledWorkstreams.length ? t.entitledWorkstreams.map((w) => <Chip key={w} label={w} />) : <span className="text-slate-400 text-[11px]">none</span>}
                      </div>
                    </td>
                    <td className="py-2.5 px-2">
                      <div className="flex items-center justify-end gap-1.5">
                        <button onClick={() => openEdit('tenant', t.portcoId)} className={ghostBtn}>Edit</button>
                        <button onClick={() => deleteBiz(t)} className={dangerBtn}><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {tenants.length === 0 && !loading && <tr><td colSpan={5} className="py-6 text-center text-slate-400">No businesses yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Catalog */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-mantis-600" />
          <span className="text-xs font-semibold text-slate-700">Workstream catalog:</span>
          <span className="text-xs text-slate-500">{catalog.join(' · ')}</span>
        </div>
      </div>

      {/* ---- Entitlements edit modal ---- */}
      {edit && (
        <Modal title={`${edit.kind === 'tenant' ? 'Business' : 'Firm'}: ${edit.name}`} onClose={() => setEdit(null)}>
          <p className="text-xs text-slate-500 mb-3">
            {edit.kind === 'tenant'
              ? 'Choose the purchased workstreams. Clear to inherit the firm default.'
              : 'Choose the default workstreams inherited by this firm\u2019s businesses.'}
          </p>
          {edit.kind === 'tenant' && edit.current === null && (
            <div className="text-[11px] text-sky-700 bg-sky-50 border border-sky-200 rounded-lg px-2.5 py-1.5 mb-3">
              Currently inheriting the firm default — checking anything below switches this business to an explicit purchased list.
            </div>
          )}
          {edit.kind === 'tenant' && edit.current !== null && (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mb-3">
              This business has an explicit purchased list — it overrides the firm default. Use “Clear (inherit firm)” to go back to following the firm.
            </div>
          )}
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 my-3">
            {catalog.map((w) => (
              <label key={w} className="flex items-center gap-2 text-xs text-slate-700 py-1 cursor-pointer">
                <input type="checkbox" checked={editSel.includes(w)}
                  onChange={(e) => setEditSel(e.target.checked ? [...editSel, w] : editSel.filter((x) => x !== w))} />
                {w}
              </label>
            ))}
          </div>
          {edit.kind === 'firm' && (() => {
            const explicit = tenants.filter((t) => t.firmId === edit.id && t.source === 'tenant');
            if (!explicit.length) return null;
            return (
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mb-3">
                <b>Not affected by defaults:</b> {explicit.map((t) => t.name || t.portcoId).join(', ')} — {explicit.length === 1 ? 'it has' : 'they have'} explicit purchased lists. Edit {explicit.length === 1 ? 'it' : 'each'} from the Businesses table below.
              </div>
            );
          })()}
          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
            {edit.kind === 'tenant' && (
              <button onClick={() => saveEdit(true)} className="mr-auto px-3 py-1.5 text-[11px] font-medium text-red-600 hover:bg-red-50 border border-red-200 rounded-lg">Clear (inherit firm)</button>
            )}
            <button onClick={() => setEdit(null)} className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-800">Cancel</button>
            <button onClick={() => saveEdit(false)} className="px-3 py-1.5 text-xs font-semibold bg-mantis-600 hover:bg-mantis-500 text-white rounded-lg">Save</button>
          </div>
        </Modal>
      )}

      {/* ---- New firm modal ---- */}
      {newFirm && (
        <Modal title="Onboard a new firm" onClose={() => setNewFirm(null)}>
          <p className="text-xs text-slate-500 mb-3">Creates the firm. Assign businesses and set defaults right after.</p>
          <label className="text-xs text-slate-600">Firm name
            <input className={`${inputCls} mt-1 mb-3`} value={newFirm.name} placeholder="e.g. Copperline Capital"
              onChange={(e) => setNewFirm({ ...newFirm, name: e.target.value })} autoFocus />
          </label>
          <label className="text-[11px] text-slate-500">Code (optional)
            <input className={`${inputCls} mt-1 mb-4`} value={newFirm.code} placeholder="e.g. CPL"
              onChange={(e) => setNewFirm({ ...newFirm, code: e.target.value })} />
          </label>
          <div className="flex justify-end gap-2">
            <button onClick={() => setNewFirm(null)} className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-800">Cancel</button>
            <button onClick={createFirm} disabled={!newFirm.name.trim()} className="px-3 py-1.5 text-xs font-semibold bg-mantis-600 hover:bg-mantis-500 disabled:opacity-50 text-white rounded-lg">Create firm</button>
          </div>
        </Modal>
      )}

      {/* ---- Assign businesses modal ---- */}
      {assign && (
        <Modal title={`Businesses — ${assign.name}`} onClose={() => setAssign(null)}>
          <p className="text-xs text-slate-500 mb-2">Check the businesses that belong to this firm. A business already in another firm moves over.</p>
          <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-lg px-3 py-1.5 my-2">
            {tenants.map((t) => (
              <label key={t.portcoId} className="flex items-center gap-2.5 text-xs py-1.5 cursor-pointer border-b border-slate-100 last:border-b-0">
                <input type="checkbox" checked={assignSel.includes(t.portcoId)}
                  onChange={(e) => setAssignSel(e.target.checked ? [...assignSel, t.portcoId] : assignSel.filter((x) => x !== t.portcoId))} />
                <span className="text-slate-700">{t.name || t.portcoId}</span>
                <span className="ml-auto text-[10px] text-slate-400">
                  {t.firmId && t.firmId !== assign.firmId ? `in ${firms.find((f) => f.firmId === t.firmId)?.name || t.firmId}` : ''}
                </span>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setAssign(null)} className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-800">Cancel</button>
            <button onClick={saveAssign} className="px-3 py-1.5 text-xs font-semibold bg-mantis-600 hover:bg-mantis-500 text-white rounded-lg">Save assignment</button>
          </div>
        </Modal>
      )}

      {/* ---- Create firm admin modal ---- */}
      {adminFor && (
        <Modal title={`Create firm admin — ${adminFor.name}`} onClose={() => setAdminFor(null)}>
          <p className="text-xs text-slate-500 mb-3">The firm admin sees every business in the firm and manages their staff logins from Settings → User Management.</p>
          <label className="text-xs text-slate-600">Name
            <input className={`${inputCls} mt-1 mb-3`} value={adminForm.name} placeholder="e.g. Jordan Ellis"
              onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })} autoFocus />
          </label>
          <label className="text-xs text-slate-600">Email
            <input type="email" className={`${inputCls} mt-1 mb-3`} value={adminForm.email} placeholder="admin@firm.com"
              onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })} />
          </label>
          <label className="text-xs text-slate-600">Temporary password <span className="text-slate-400">(8+ characters)</span>
            <input className={`${inputCls} mt-1 mb-4`} value={adminForm.password} placeholder="they change it after first sign-in"
              onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })} />
          </label>
          <div className="flex justify-end gap-2">
            <button onClick={() => setAdminFor(null)} className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-800">Cancel</button>
            <button onClick={createAdmin} disabled={!adminForm.name || !adminForm.email || adminForm.password.length < 8}
              className="px-3 py-1.5 text-xs font-semibold bg-mantis-600 hover:bg-mantis-500 disabled:opacity-50 text-white rounded-lg">Create admin</button>
          </div>
        </Modal>
      )}

      {/* ---- New business modal ---- */}
      {newBiz && (
        <Modal title="Onboard a new business" onClose={() => setNewBiz(null)}>
          <p className="text-xs text-slate-500 mb-3">Creates the business with its own Key Vault encryption key. Connect its EHR afterwards in Settings → Integrations.</p>
          <label className="text-xs text-slate-600">Business name
            <input className={`${inputCls} mt-1 mb-3`} value={newBiz.name} placeholder="e.g. Cedar Ridge Therapy"
              onChange={(e) => setNewBiz({ ...newBiz, name: e.target.value })} autoFocus />
          </label>
          <label className="text-[11px] text-slate-500">ID (optional — auto-generated)
            <input className={`${inputCls} mt-1 mb-3`} value={newBiz.id} placeholder="portco_cedar-ridge"
              onChange={(e) => setNewBiz({ ...newBiz, id: e.target.value })} />
          </label>
          <label className="text-xs text-slate-600">Firm
            <select className={`${inputCls} mt-1 mb-4`} value={newBiz.firmId} onChange={(e) => setNewBiz({ ...newBiz, firmId: e.target.value })}>
              <option value="">No firm yet</option>
              {firms.map((f) => <option key={f.firmId} value={f.firmId}>{f.name}</option>)}
            </select>
          </label>
          <div className="flex justify-end gap-2">
            <button onClick={() => setNewBiz(null)} className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-800">Cancel</button>
            <button onClick={createBiz} disabled={!newBiz.name.trim()}
              className="px-3 py-1.5 text-xs font-semibold bg-mantis-600 hover:bg-mantis-500 disabled:opacity-50 text-white rounded-lg">Create business</button>
          </div>
        </Modal>
      )}
    </div>
  );
};
